"use client";

import { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useRouter } from "next/navigation";
import { AnimatePresence, motion } from "framer-motion";
import type { CatalogItem } from "@/lib/airtable";
import { addFavorite, addReaction, addSuperLike } from "@/hooks/useFavorites";
import { shouldPromptSaveFavorites } from "@/lib/emailCapture";
import { CatalogCard } from "./CatalogCard";
import { SaveFavoritesModal, SAVE_FAVORITES_DISMISSED_KEY } from "./SaveFavoritesModal";

const USER_STORAGE_KEY = "vault88-user";

// Reads localStorage to decide whether the "save your favorites" prompt should
// appear when leaving the deck. Evaluated at exit time so it reflects any email
// captured during the session.
function shouldShowSavePrompt(likedCount: number): boolean {
  let hasEmail = false;
  let dismissed = false;
  try {
    const raw = window.localStorage.getItem(USER_STORAGE_KEY);
    hasEmail = raw ? Boolean((JSON.parse(raw) as { email?: string }).email) : false;
    dismissed = window.localStorage.getItem(SAVE_FAVORITES_DISMISSED_KEY) === "1";
  } catch (error) {
    console.error("[SwipeDeck] Failed to read save-prompt state", error);
  }
  return shouldPromptSaveFavorites({ likedCount, hasEmail, dismissed });
}

type Props = {
  item: CatalogItem | null;
  onLike: () => void;
  onDislike: () => void;
  onUndo: () => void;
  canUndo: boolean;
  onReset: () => void;
  remainingCount: number;
  likedItems: CatalogItem[];
  dislikedItems: CatalogItem[];
};

const SELECTIONS_STORAGE_KEY = "vault88-selections";

export function SwipeDeck({ item, onLike, onDislike, onUndo, canUndo, onReset, remainingCount, likedItems, dislikedItems }: Props) {
  const [swipeDirection, setSwipeDirection] = useState<"left" | "right" | "up" | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffsetX, setDragOffsetX] = useState(0);
  const [dragOffsetY, setDragOffsetY] = useState(0);
  const [superLikeFlash, setSuperLikeFlash] = useState(false);
  const [navConfirmItem, setNavConfirmItem] = useState<CatalogItem | null>(null);
  const [showSavePrompt, setShowSavePrompt] = useState(false);
  const router = useRouter();

  const handleExit = () => {
    if (shouldShowSavePrompt(likedItems.length)) {
      setShowSavePrompt(true);
      return;
    }
    router.push("/browse");
  };

  const likeOpacity = swipeDirection === "right"
    ? 1
    : Math.min(1, Math.max(0, (dragOffsetX - 30) / 60));
  const dislikeOpacity = swipeDirection === "left"
    ? 1
    : Math.min(1, Math.max(0, (-dragOffsetX - 30) / 60));
  const superLikeOpacity = swipeDirection === "up"
    ? 1
    : Math.min(1, Math.max(0, (-dragOffsetY - 30) / 60));

  const triggerLike = () => {
    if (!item) return;
    addFavorite({
      id: item.id,
      itemName: item.itemName,
      pictureUrl: item.pictureUrl,
      tagPrice: item.tagPrice,
      metal: item.metal,
      stone: item.stone,
      soldOut: item.soldOut
    });
    setSwipeDirection("right");
    setTimeout(() => {
      onLike();
      setSwipeDirection(null);
    }, 180);
  };

  const triggerDislike = () => {
    if (!item) return;
    // Persist for logged-in users; addReaction is a no-op for dislikes when
    // the user is logged out (no localStorage, no migration).
    addReaction(
      {
        id: item.id,
        itemName: item.itemName,
        pictureUrl: item.pictureUrl,
        tagPrice: item.tagPrice,
        metal: item.metal,
        stone: item.stone,
        soldOut: item.soldOut,
      },
      "dislike"
    );
    setSwipeDirection("left");
    setTimeout(() => {
      onDislike();
      setSwipeDirection(null);
    }, 180);
  };

  const triggerSuperLike = () => {
    if (!item) return;
    addSuperLike({
      id: item.id,
      itemName: item.itemName,
      pictureUrl: item.pictureUrl,
      tagPrice: item.tagPrice,
      metal: item.metal,
      stone: item.stone,
      soldOut: item.soldOut
    });
    setSuperLikeFlash(true);
    setTimeout(() => setSuperLikeFlash(false), 400);
    setSwipeDirection("up");
    setTimeout(() => {
      onLike();
      setSwipeDirection(null);
    }, 180);
  };

  const cardVariants = useMemo(
    () => ({
      initial: { opacity: 0, y: 30, rotate: 0, scale: 0.98 },
      animate: { opacity: 1, y: 0, rotate: 0, scale: 1 },
      exit: {
        opacity: 0,
        x: swipeDirection === "right" ? 200 : swipeDirection === "left" ? -200 : 0,
        y: swipeDirection === "up" ? -200 : 0,
        rotate: swipeDirection === "right" ? 12 : swipeDirection === "left" ? -12 : 0,
        scale: 0.95,
        transition: { duration: 0.2 }
      }
    }),
    [swipeDirection]
  );

  const handleDrag = (_: any, info: { offset: { x: number; y: number } }) => {
    setDragOffsetX(info.offset.x);
    setDragOffsetY(info.offset.y);
  };

  const handleDragEnd = (
    _: any,
    info: { offset: { x: number; y: number }; velocity: { x: number; y: number } }
  ) => {
    setIsDragging(false);
    setDragOffsetX(0);
    setDragOffsetY(0);
    if (!item) return;
    const { x, y } = info.offset;
    const { x: vx, y: vy } = info.velocity;
    if ((y < -120 || vy < -800) && Math.abs(y) > Math.abs(x)) {
      triggerSuperLike();
    } else if (x > 120 || vx > 800) {
      triggerLike();
    } else if (x < -120 || vx < -800) {
      triggerDislike();
    }
  };

  const handleViewSelections = () => {
    if (typeof window !== "undefined") {
      try {
        window.localStorage.setItem(
          SELECTIONS_STORAGE_KEY,
          JSON.stringify({ liked: likedItems, disliked: dislikedItems })
        );
      } catch (error) {
        console.error("[SwipeDeck] Failed to cache selections", error);
      }
    }
    router.push("/browse/selections");
  };

  return (
    <div className="flex flex-col items-center justify-start gap-4 px-4 pt-2 pb-4">
      <AnimatePresence mode="popLayout" initial={false}>
        {item ? (
          <motion.div
            key={item.id}
            className="w-full max-w-md mx-auto cursor-pointer"
            variants={cardVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={{ type: "spring", stiffness: 260, damping: 22 }}
            drag
            dragConstraints={{ left: 0, right: 0, top: 0, bottom: 0 }}
            dragElastic={0.25}
            onDragStart={() => setIsDragging(true)}
            onDrag={handleDrag}
            onDragEnd={handleDragEnd}
            whileTap={{ scale: 0.98 }}
            onClick={() => item && !isDragging && setNavConfirmItem(item)}
          >
            <div className="relative">
              <div
                className="pointer-events-none absolute left-4 top-5 z-10 -rotate-[20deg] rounded border-4 border-[#4ade80] px-2 py-0.5"
                style={{ opacity: likeOpacity }}
              >
                <svg viewBox="0 0 24 24" className="inline h-7 w-7 text-[#4ade80]" fill="currentColor" aria-hidden="true">
                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                </svg>
              </div>
              <div
                className="pointer-events-none absolute right-4 top-5 z-10 rotate-[20deg] rounded border-4 border-red-400 px-2 py-0.5"
                style={{ opacity: dislikeOpacity }}
              >
                <svg viewBox="0 0 24 24" className="inline h-7 w-7 text-red-400" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" aria-hidden="true">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </div>
              <div
                className="pointer-events-none absolute left-1/2 top-5 z-10 -translate-x-1/2 rounded border-4 border-[#c4a668] px-2 py-0.5"
                style={{ opacity: superLikeOpacity }}
              >
                <span className="text-sm font-bold uppercase tracking-wide text-[#c4a668]">Super Like</span>
              </div>
              <CatalogCard item={item} imageFit="contain" />
              {/* Super-like button */}
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); triggerSuperLike(); }}
                className="absolute right-3 bottom-3 z-20 flex h-10 w-10 items-center justify-center rounded-full bg-[#c4a668] text-white shadow-lg transition hover:scale-110 active:scale-95"
                aria-label="Super like"
              >
                <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor" aria-hidden="true">
                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                  <line x1="12" y1="8" x2="12" y2="16" stroke="white" strokeWidth="2" strokeLinecap="round" />
                  <line x1="8" y1="12" x2="16" y2="12" stroke="white" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </button>
              {/* Super-like flash */}
              <AnimatePresence>
                {superLikeFlash && (
                  <motion.div
                    key="super-flash"
                    initial={{ opacity: 0.8 }}
                    animate={{ opacity: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.4 }}
                    className="pointer-events-none absolute inset-0 z-10 rounded-xl bg-[#c4a668]/20"
                  />
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="empty"
            className="glass shadow-soft rounded-3xl border border-slate-200 p-10 w-full max-w-md text-center space-y-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <div className="text-3xl font-semibold text-royal">You&apos;ve seen everything</div>
            <p className="text-slate-600">
              Tap reset to start again or check back soon for fresh pieces curated to your taste.
            </p>
            <button
              onClick={onReset}
              className="w-full rounded-sm bg-royal text-white px-5 py-3 font-semibold shadow-soft hover:bg-royal/90 transition"
            >
              Reset deck
            </button>
            {(likedItems.length > 0 || dislikedItems.length > 0) && (
              <button
                type="button"
                className="w-full rounded-sm border border-slate-200 px-5 py-3 font-semibold text-royal shadow-soft transition hover:-translate-y-0.5"
                onClick={handleViewSelections}
              >
                View my selections
              </button>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      <div className="flex items-center gap-3 w-full max-w-md">
        <button
          type="button"
          onClick={onUndo}
          disabled={!canUndo}
          aria-label="Go back to the previous piece"
          className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white text-royal shadow-soft transition hover:-translate-y-0.5 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:translate-y-0"
        >
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M9 14 4 9l5-5" />
            <path d="M4 9h11a5 5 0 0 1 0 10h-1" />
          </svg>
        </button>
        <button
          type="button"
          onClick={triggerDislike}
          disabled={!item}
          className="flex-1 rounded-sm border border-slate-200 bg-white py-3 font-semibold text-royal shadow-soft hover:-translate-y-0.5 transition disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Dislike
        </button>
        <button
          type="button"
          onClick={triggerLike}
          disabled={!item}
          className="flex-1 rounded-sm bg-[#c4a668] py-3 font-semibold text-white shadow-soft hover:-translate-y-0.5 transition disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Like
        </button>
      </div>
      <div className="text-sm text-slate-500">
        {item ? `${remainingCount} more curated pieces waiting` : "Deck completed"}
      </div>
      <button
        type="button"
        onClick={handleExit}
        className="text-xs uppercase tracking-[0.3em] text-slate-500 transition hover:text-slate-900 hover:underline hover:underline-offset-4"
      >
        Back to browsing
      </button>

      {/* Navigation confirmation modal — portaled to body to escape main's z-10 stacking context */}
      {createPortal(
        <AnimatePresence>
          {navConfirmItem && (
            <motion.div
              key="nav-confirm"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="fixed inset-0 z-50 flex items-center justify-center px-4"
            >
              <button
                type="button"
                aria-label="Close"
                className="absolute inset-0 bg-black/40"
                onClick={() => setNavConfirmItem(null)}
              />
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 10 }}
                transition={{ duration: 0.15 }}
                className="relative w-full max-w-sm rounded-sm border border-slate-200 bg-white p-6 text-center shadow-soft"
              >
                <h3 className="text-lg font-semibold text-slate-900">Leave the Swipe Deck?</h3>
                <p className="mt-2 text-sm text-slate-600">
                  You&apos;ll be navigating to this piece&apos;s detail page. Your swipe progress may be lost.
                </p>
                <div className="mt-5 flex flex-col gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      const id = navConfirmItem.id;
                      setNavConfirmItem(null);
                      router.push(`/pieces/${id}`);
                    }}
                    className="w-full rounded-sm bg-royal px-5 py-3 text-xs font-semibold uppercase tracking-[0.3em] text-white shadow-soft transition hover:-translate-y-0.5"
                  >
                    View piece
                  </button>
                  <button
                    type="button"
                    onClick={() => setNavConfirmItem(null)}
                    className="w-full rounded-sm border border-slate-200 px-5 py-3 text-xs font-semibold uppercase tracking-[0.3em] text-slate-700 transition hover:border-slate-400 hover:text-slate-900"
                  >
                    Stay in deck
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}

      <SaveFavoritesModal open={showSavePrompt} onClose={() => router.push("/browse")} />
    </div>
  );
}
